import { useEffect, useState } from "react";
import { useRule } from "../CreateRuleNew/RuleReducer";
import { useDispatch, useSelector } from "react-redux";
import { getSnapshotCameras } from "../../../../../Api Methods/Api";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import classes from "./RuleWizard.module.css";

const CameraRow = ({ id, name, subtitle, isSelected, onToggle }) => (
    <div
        style={{
            display: "flex", alignItems: "center", gap: "14px",
            padding: "14px 18px",
            border: `2px solid ${isSelected ? "var(--Primary-Color)" : "#e0e0e0"}`,
            borderRadius: "8px", cursor: "pointer",
            background: isSelected ? "rgba(var(--Primary-Color-RGB, 33,150,243), 0.05)" : undefined,
            transition: "border-color 0.15s ease",
        }}
        onClick={onToggle}
    >
        <Checkbox checked={isSelected} onChange={() => {}} />
        <div>
            <p style={{ margin: 0, fontWeight: 600, fontSize: "14px", color: "#2d2d2d" }}>{name}</p>
            {subtitle && <p style={{ margin: "3px 0 0 0", fontSize: "12px", color: "#9e9e9e" }}>{subtitle}</p>}
        </div>
    </div>
);

const SectionHeader = ({ label }) => (
    <p style={{ margin: "12px 0 4px", fontSize: "11px", fontWeight: 600, color: "var(--text-label)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
    </p>
);

const WizardStepSnapshot = ({ onNext, onBack }) => {
    const { state, dispatch } = useRule();
    const token = useSelector(s => s.auth.token);
    const client = useSelector(s => s.auth.client);
    const apiDispatch = useDispatch();

    const [ajaxCameras, setAjaxCameras] = useState([]);
    const [dahuaCameras, setDahuaCameras] = useState([]);
    const [hikvisionCameras, setHikvisionCameras] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCameras = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await getSnapshotCameras({ token, client }, state.deviceUUID, apiDispatch);
                const data = response?.data ?? {};

                console.log(data);

                // Flatten AJAX: one selectable item per snapshot_device
                const ajax = (data.ajax ?? []).flatMap(hub =>
                    (hub.snapshot_devices ?? []).map(device => ({
                        id: `ajax_${device.deviceId}`,
                        name: device.deviceName,
                        subtitle: device.deviceType,
                        type: "ajax",
                        hub_id: hub.hub_id,
                        pin: hub.pin,
                        deviceId: device.deviceId,
                    }))
                );

                // Flatten Dahua: one selectable item per channel
                const dahua = (data.dahua ?? []).flatMap(entry =>
                    (entry.channels ?? []).map(ch => ({
                        id: `dahua_${entry.pin}_${ch.channel}`,
                        name: ch.name,
                        subtitle: `Channel ${ch.channel} · ${entry.ipAndPort}`,
                        type: "dahua",
                        pin: entry.pin,
                        channel: ch.channel,
                        ipAndPort: entry.ipAndPort,
                    }))
                );

                // Flatten Hikvision: one selectable item per channel
                const hikvision = (data.hikvision ?? []).flatMap(entry =>
                    (entry.channels ?? []).map(ch => ({
                        id: `hikvision_${entry.pin}_${ch.channel}`,
                        name: ch.name,
                        subtitle: `Channel ${ch.channel} · ${entry.ipAndPort}`,
                        type: "hikvision",
                        pin: entry.pin,
                        channel: ch.channel,
                        ipAndPort: entry.ipAndPort,
                    }))
                );

                setAjaxCameras(ajax);
                setDahuaCameras(dahua);
                setHikvisionCameras(hikvision);
            } catch {
                setError("Failed to load cameras.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchCameras();
    }, [state.deviceUUID]);

    const selectedIds = state.selectedSnapshotCameras.map(c => c.id);

    const toggleCamera = (camera) => {
        const isSelected = selectedIds.includes(camera.id);
        const next = isSelected
            ? state.selectedSnapshotCameras.filter(c => c.id !== camera.id)
            : [...state.selectedSnapshotCameras, camera];
        dispatch({ type: "SET_SELECTED_SNAPSHOT_CAMERAS", payload: next });
    };

    const handleNext = () => {
        const PROVIDER_MAP = { ajax: "AJAX", dahua: "DAHUA", hikvision: "HIKVISION" };
        const snapshotTargets = state.selectedSnapshotCameras.map(c => ({
            provider: PROVIDER_MAP[c.type] ?? c.type.toUpperCase(),
            site_uuid: state.deviceUUID,
            pin: c.pin,
            channel: c.type === "dahua" ? c.channel : null,
        }));

        const snapshotAction = {
            action_type: "CREATE_SNAPSHOT",
            site_to: state.deviceUUID,
            execution_order: state.actions.filter(a => a.action_type !== "CREATE_SNAPSHOT").length + 1,
            delay_seconds: 0,
            snapshot_targets: snapshotTargets,
        };
        dispatch({
            type: "SET_ADV_RULE_ACTION",
            payload: [
                ...state.actions.filter(a => a.action_type !== "CREATE_SNAPSHOT"),
                snapshotAction,
            ],
        });
        onNext();
    };

    const hasAny = ajaxCameras.length > 0 || dahuaCameras.length > 0 || hikvisionCameras.length > 0;

    return (
        <>
            <div className={classes.contentHeader}>
                <h2 className={classes.contentTitle}>Select snapshot cameras</h2>
                <p className={classes.contentSubtitle}>Choose which cameras should take a snapshot when this rule triggers</p>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                {isLoading && (
                    <p style={{ color: "var(--text-label)", fontSize: "14px" }}>Loading cameras…</p>
                )}

                {error && (
                    <p style={{ color: "#ee6d6d", fontSize: "14px" }}>{error}</p>
                )}

                {!isLoading && !error && !hasAny && (
                    <p style={{ color: "var(--text-label)", fontSize: "14px", textAlign: "center", marginTop: "2rem" }}>
                        No snapshot cameras available for this site.
                    </p>
                )}

                {!isLoading && ajaxCameras.length > 0 && (
                    <>
                        <SectionHeader label="AJAX Cameras" />
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {ajaxCameras.map(camera => (
                                <CameraRow
                                    key={camera.id}
                                    {...camera}
                                    isSelected={selectedIds.includes(camera.id)}
                                    onToggle={() => toggleCamera(camera)}
                                />
                            ))}
                        </div>
                    </>
                )}

                {!isLoading && dahuaCameras.length > 0 && (
                    <>
                        <SectionHeader label="Dahua Cameras" />
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {dahuaCameras.map(camera => (
                                <CameraRow
                                    key={camera.id}
                                    {...camera}
                                    isSelected={selectedIds.includes(camera.id)}
                                    onToggle={() => toggleCamera(camera)}
                                />
                            ))}
                        </div>
                    </>
                )}

                {!isLoading && hikvisionCameras.length > 0 && (
                    <>
                        <SectionHeader label="Hikvision Cameras" />
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {hikvisionCameras.map(camera => (
                                <CameraRow
                                    key={camera.id}
                                    {...camera}
                                    isSelected={selectedIds.includes(camera.id)}
                                    onToggle={() => toggleCamera(camera)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <div className={classes.navRow}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} />
                <Button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    severity="success"
                    disabled={state.selectedSnapshotCameras.length === 0}
                    onClick={handleNext}
                />
            </div>
        </>
    );
};

export default WizardStepSnapshot;
